import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Users, 
  Building2, 
  Car, 
  UserSquare2,
  Eye,
  MoreVertical,
  Plus,
  Info,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import Drawer from '../../components/common/Drawer/Drawer';
import styles from './Directory.module.css';
import api from '../../api/axios';

const Directory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const normalized = path.replace(/\\/g, '/');
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
  };

  const [dbStats, setDbStats] = useState({
    totalResidents: 0,
    totalFlats: 0,
    totalVehicles: 0,
    totalFamilyMembers: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/security/directory');
      setResidents(data.residents);
      setDbStats(data.stats);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const stats = [
    { title: 'Total Residents', value: dbStats.totalResidents, desc: 'Active Residents', icon: Users, colorClass: styles.iconBlue },
    { title: 'Total Flats', value: dbStats.totalFlats, desc: 'Occupied Flats', icon: Building2, colorClass: styles.iconGreen },
    { title: 'Total Vehicles', value: dbStats.totalVehicles, desc: 'Registered Vehicles', icon: Car, colorClass: styles.iconPurple },
    { title: 'Total Family Members', value: dbStats.totalFamilyMembers, desc: 'Across All Flats', icon: UserSquare2, colorClass: styles.iconOrange },
  ];

  // Pagination and Filtering Logic
  const filteredResidents = residents.filter(r => 
    (r.fullName && r.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (r.flatNumber && String(r.flatNumber).includes(searchTerm)) ||
    (r.phone && String(r.phone).includes(searchTerm))
  );

  const totalPages = Math.ceil(filteredResidents.length / rowsPerPage);
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentResidents = filteredResidents.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <h1>Resident Directory</h1>
        <p>View and manage all society residents, flats, and vehicles</p>
      </div>

      <div className={styles.statsRow}>
        {stats.map((stat, index) => (
          <div key={index} className={styles.statCard}>
            <div className={`${styles.iconWrapper} ${stat.colorClass}`}>
              <stat.icon size={24} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statTitle}>{stat.title}</span>
              <span className={styles.statValue}>{stat.value}</span>
              <span className={styles.statDesc}>{stat.desc}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.mainCard}>
        <div className={styles.filtersRow}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input 
              type="text" 
              placeholder="Search by name, flat no., or phone" 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
            />
          </div>
          
          <select className={styles.selectBox}>
            <option>All Blocks</option>
            <option>Block A</option>
            <option>Block B</option>
            <option>Block C</option>
          </select>

          <select className={styles.selectBox}>
            <option>All Flats</option>
          </select>

          <select className={styles.selectBox}>
            <option>Status: All</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>

          <button className={styles.addBtn}>
            <Plus size={16} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '4px' }} /> Add Resident
          </button>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Resident Name</th>
                <th>Flat / Unit</th>
                <th>Block</th>
                <th>Phone Number</th>
                <th>Vehicles</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>Loading...</td>
                </tr>
              ) : currentResidents.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>No residents found.</td>
                </tr>
              ) : currentResidents.map((r) => (
                <tr key={r._id}>
                  <td>
                    <div className={styles.residentInfo}>
                      {r.profileImage ? (
                        <img src={getImageUrl(r.profileImage)} alt={r.fullName} className={styles.avatar} />
                      ) : (
                        <div className={styles.avatarInitials}>{r.fullName ? r.fullName.charAt(0) : 'U'}</div>
                      )}
                      <div className={styles.details}>
                        <div className={styles.nameRow}>
                          <strong>{r.fullName}</strong>
                          <span className={`${styles.badgeType} ${styles[(r.residentType || 'Owner').toLowerCase()]}`}>{r.residentType || 'Owner'}</span>
                        </div>
                        <span className={styles.email}>{r.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>{r.flatNumber ? `${r.wing || ''}-${r.flatNumber}` : '-'}</td>
                  <td>{r.wing ? `Block ${r.wing}` : '-'}</td>
                  <td>{r.phone || '-'}</td>
                  <td>
                    {r.vehicles && r.vehicles.length > 0 ? (
                      <div className={styles.vehicleBadge}>
                        <span>{r.vehicles[0]}</span>
                        {r.vehicles.length > 1 && <span className={styles.extra}>+{r.vehicles.length - 1}</span>}
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles.statusActive}`}>
                      Active
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button className={styles.actionBtn} onClick={() => { setSelectedResident(r); setIsDrawerOpen(true); }}>
                        <Eye size={16} />
                      </button>
                      <button className={styles.actionBtn} onClick={() => toast('More options coming soon', { icon: 'ℹ️' })}>
                        <MoreVertical size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileList}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
          ) : currentResidents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>No residents found.</div>
          ) : currentResidents.map((r) => (
            <div key={r._id} className={styles.mobileCard}>
              <div className={styles.mobileCardHeader}>
                <div className={styles.residentInfo}>
                  {r.profileImage ? (
                    <img src={getImageUrl(r.profileImage)} alt={r.fullName} className={styles.avatar} style={{ width: '36px', height: '36px' }} />
                  ) : (
                    <div className={styles.avatarInitials} style={{ width: '36px', height: '36px', fontSize: '14px' }}>{r.fullName ? r.fullName.charAt(0) : 'U'}</div>
                  )}
                  <div className={styles.details}>
                    <div className={styles.nameRow}>
                      <strong style={{ fontSize: '14px', color: '#0f172a' }}>{r.fullName}</strong>
                      <span className={`${styles.badgeType} ${styles[(r.residentType || 'Owner').toLowerCase()]}`}>{r.residentType || 'Owner'}</span>
                    </div>
                  </div>
                </div>
                <span className={`${styles.statusBadge} ${styles.statusActive}`}>
                  Active
                </span>
              </div>
              
              <div className={styles.mobileCardMeta}>
                <div className={styles.metaRow}>
                  <span>Flat: <strong style={{ color: '#0f172a' }}>{r.flatNumber ? `${r.wing || ''}-${r.flatNumber}` : '-'}</strong></span>
                  <span>Phone: <strong style={{ color: '#0f172a' }}>{r.phone || '-'}</strong></span>
                </div>
                <div className={styles.metaRow}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>Vehicles:</span>
                    {r.vehicles && r.vehicles.length > 0 ? (
                      <div className={styles.vehicleBadge} style={{ margin: 0 }}>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{r.vehicles[0]}</span>
                        {r.vehicles.length > 1 && <span className={styles.extra} style={{ padding: '0px 4px' }}>+{r.vehicles.length - 1}</span>}
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </div>
                </div>
                <div className={styles.metaRow} style={{ marginTop: '4px' }}>
                  <button className={styles.actionBtn} style={{ width: '100%', height: '36px', borderRadius: '8px', background: '#f8fafc', color: '#334155', fontWeight: 600, border: '1px solid #e2e8f0' }} onClick={() => { setSelectedResident(r); setIsDrawerOpen(true); }}>
                    <Eye size={16} style={{ marginRight: '6px' }} /> View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.pagination}>
          <div className={styles.pageInfo}>
            Showing {filteredResidents.length > 0 ? indexOfFirst + 1 : 0} to {Math.min(indexOfLast, filteredResidents.length)} of {filteredResidents.length} residents
          </div>
          <div className={styles.pageControls}>
            <button 
              className={styles.pageBtn} 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{ opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              <ChevronLeft size={16} />
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
              // Simple pagination logic for showing limited page numbers
              if (
                page === 1 || 
                page === totalPages || 
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button 
                    key={page}
                    className={`${styles.pageBtn} ${currentPage === page ? styles.active : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              } else if (
                page === currentPage - 2 || 
                page === currentPage + 2
              ) {
                return <span key={page} className={styles.pageDots}>...</span>;
              }
              return null;
            })}

            <button 
              className={styles.pageBtn} 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{ opacity: currentPage === totalPages || totalPages === 0 ? 0.5 : 1, cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer' }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.footerHint}>
        <Info size={16} color="#3b82f6" />
        Click on the eye icon to view detailed resident profile, family members, vehicles & gate access history.
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => { setIsDrawerOpen(false); setSelectedResident(null); }} title="Resident Details">
        {selectedResident && (
          <div className={styles.drawerContent}>
            <div className={styles.drawerHeader}>
              {selectedResident.profileImage ? (
                <img src={getImageUrl(selectedResident.profileImage)} alt="Profile" className={styles.drawerAvatar} />
              ) : (
                <div className={styles.drawerAvatarFallback}>
                  {selectedResident.fullName ? selectedResident.fullName.charAt(0) : 'U'}
                </div>
              )}
              <div>
                <h3 className={styles.drawerName}>{selectedResident.fullName}</h3>
                <p className={styles.drawerRole}>{selectedResident.residentType || 'Owner'} &bull; Block {selectedResident.wing || '-'} Flat {selectedResident.flatNumber || '-'}</p>
              </div>
            </div>

            <div className={styles.drawerSection}>
              <div className={styles.drawerSectionTitle}>Contact Information</div>
              <div className={styles.drawerRow}>
                <span className={styles.drawerLabel}>Phone</span>
                <span className={styles.drawerValue}>{selectedResident.phone || 'Not provided'}</span>
              </div>
              <div className={styles.drawerRow}>
                <span className={styles.drawerLabel}>Email</span>
                <span className={styles.drawerValue}>{selectedResident.email}</span>
              </div>
            </div>

            <div className={styles.drawerSection}>
              <div className={styles.drawerSectionTitle}>Registered Vehicles</div>
              {selectedResident.vehicles && selectedResident.vehicles.length > 0 ? (
                <ul className={styles.drawerList}>
                  {selectedResident.vehicles.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              ) : (
                <div className={styles.drawerLabel} style={{ fontStyle: 'italic' }}>No vehicles registered</div>
              )}
            </div>
            
            <div className={styles.drawerSection}>
              <div className={styles.drawerSectionTitle}>Family Members ({selectedResident.familyCount || 0})</div>
              {selectedResident.familyMembers && selectedResident.familyMembers.length > 0 ? (
                <div className={styles.familyGrid}>
                  {selectedResident.familyMembers.map((member, i) => (
                    <div key={i} className={styles.familyCard}>
                      <span className={styles.familyName}>{member.name}</span>
                      <span className={styles.familyRelation}>{member.relation} &bull; {member.age} yrs</span>
                      {member.phone && <span className={styles.familyPhone}>{member.phone}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.drawerRow}>
                  <span className={styles.drawerLabel} style={{ fontStyle: 'italic' }}>No family members registered</span>
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Directory;
